import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import './VideoHistory.css';

interface VideoHistoryProps {
  onVideoSelect: (videoUrl: string, taskId: string) => void;
}

interface HistoryItem {
  id: string;
  task_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  filename: string;
  create_time: any | string | number;
  update_time: string;
  completed_time?: string;
  duration: string;
  upload_url: string;
  thumbnail_url?: string;
  file_size?: number;
}

// 暴露给父组件的方法接口
export interface VideoHistoryRef {
  refreshHistory: () => void;
}

// 👇 模拟你的 fetchUserHistory 函数（你可以替换成 import 的真实函数）
const fetchUserHistory = async (page: number = 1, limit: number = 20) => {
  const token = localStorage.getItem('glistar_token');
  if (!token) {
    return { data: [], total: 0, page: 1, limit };
  }

  // Debug mode
  const debugEmpty = localStorage.getItem('debug_empty_history') === 'true';
  if (debugEmpty) {
    console.log('🐛 Debug: returning empty history');
    return { data: [], total: 0, page: 1, limit };
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/user/history?page=${page}&limit=${limit}&type=2`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const result = await response.json();

    if (response.ok && result.status === 200) {
      const { list = [], total = 0 } = result.data || {};
      return { data: list, total, page, limit };
    } else {
      console.error('API error:', result.message || response.statusText);
      return { data: [], total: 0, page, limit };
    }
  } catch (error) {
    console.error('Network error:', error);
    return { data: [], total: 0, page, limit };
  }
};

const VideoHistory = forwardRef<VideoHistoryRef, VideoHistoryProps>(({ onVideoSelect }, ref) => {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const limit = 20; // 每页条数

  // 暴露刷新方法给父组件
  useImperativeHandle(ref, () => ({
    refreshHistory: () => {
      console.log('Refreshing history...');
      // 重置分页并重新加载第一页
      setCurrentPage(1);
      loadHistory(1, true);
    }
  }));

  const loadHistory = useCallback(async (page: number, isRefresh: boolean = false) => {
    setLoading(true);
    try {
      const res = await fetchUserHistory(page, limit);
      if (isRefresh || page === 1) {
        setHistoryItems(res.data);
      } else {
        setHistoryItems((prev) => [...prev, ...res.data]);
      }
      setHasMore(res.data.length === limit);
      setCurrentPage(page);
      setError(null);
    } catch (err) {
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

   // 初始加载第 1 页
  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);
  
  const handleLoadMore = () => {
    loadHistory(currentPage + 1);
  };

  const handleVideoClick = (item: HistoryItem) => {
    setSelectedVideo(item.id);
    onVideoSelect(item.upload_url, item.task_id);
  };

  return (
    <div className="video-history">
      <div className="history-header">
        <h3>History</h3>
      </div>

      <div className="history-content">
        {historyItems.length === 0 ? (
          <div className="empty-history">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M23 7L16 12L23 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <p>No videos generated yet</p>
            <span>Your video history will appear here</span>
          </div>
        ) : (
          <>
          <div className="history-list">
            {historyItems.map((item) => (
              <div
                key={item.id}
                className={`history-item ${selectedVideo === item.id ? 'selected' : ''}`}
                onClick={() => handleVideoClick(item)}
              >
                <div className="item-thumbnail">
                  <img src={item.thumbnail_url} alt={item.filename} />
                  <div className="play-overlay">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && !loading && (
              <div className="load-more">
                <button onClick={handleLoadMore} disabled={loading}>
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}

        {loading && historyItems.length > 0 && (
          <div className="loading-more">Loading more...</div>
        )}
      </div>
    </div>
  );
});

export default VideoHistory;

